import React, { useState, useEffect, useContext } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { AuthContext } from '../App';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import axios from 'axios';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const { API } = useContext(AuthContext);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/banners`);
      setBanners(response.data);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBannerClick = (banner) => {
    if (banner.link_url) {
      // Se tem link, abrir em nova aba
      window.open(banner.link_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="w-full h-64 md:h-80 bg-gradient-to-r from-green-100 to-orange-100 animate-pulse rounded-lg mb-8"></div>
    );
  }

  if (!banners || banners.length === 0) {
    return null; // Não mostra nada se não há banners
  }

  // Se há apenas um banner, mostra sem carrossel
  if (banners.length === 1) {
    const banner = banners[0];
    return (
      <div className="w-full h-64 md:h-80 mb-8 rounded-lg overflow-hidden shadow-lg relative group">
        <div 
          className={`w-full h-full bg-cover bg-center bg-no-repeat relative ${
            banner.link_url ? 'cursor-pointer' : ''
          }`}
          style={{ backgroundImage: `url(${banner.image_url})` }}
          onClick={() => handleBannerClick(banner)}
        >
          {/* Overlay escuro para melhor legibilidade */}
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          
          {/* Conteúdo do banner */}
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="text-center text-white max-w-2xl">
              <h2 className="text-2xl md:text-4xl font-bold mb-4 drop-shadow-lg">
                {banner.title}
              </h2>
              {banner.description && (
                <p className="text-lg md:text-xl mb-4 drop-shadow-lg opacity-90">
                  {banner.description}
                </p>
              )}
              {banner.link_url && (
                <div className="inline-flex items-center gap-2 text-sm md:text-base opacity-75 group-hover:opacity-100 transition-opacity">
                  <ExternalLink className="w-4 h-4" />
                  Clique para saber mais
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Para múltiplos banners, usa o carrossel
  return (
    <div className="w-full h-64 md:h-80 mb-8 relative">
      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        spaceBetween={0}
        slidesPerView={1}
        navigation={{
          prevEl: '.swiper-button-prev-custom',
          nextEl: '.swiper-button-next-custom',
        }}
        pagination={{ 
          clickable: true,
          bulletClass: 'swiper-pagination-bullet',
          bulletActiveClass: 'swiper-pagination-bullet-active'
        }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        loop={true}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        className="w-full h-full rounded-lg overflow-hidden shadow-lg"
      >
        {banners.map((banner) => (
          <SwiperSlide key={banner.id}>
            <div 
              className={`w-full h-full bg-cover bg-center bg-no-repeat relative group ${
                banner.link_url ? 'cursor-pointer' : ''
              }`}
              style={{ backgroundImage: `url(${API}${banner.image_url})` }}
              onClick={() => handleBannerClick(banner)}
            >
              {/* Overlay escuro para melhor legibilidade */}
              <div className="absolute inset-0 bg-black bg-opacity-30"></div>
              
              {/* Conteúdo do banner */}
              <div className="absolute inset-0 flex items-center justify-center p-6">
                <div className="text-center text-white max-w-2xl">
                  <h2 className="text-2xl md:text-4xl font-bold mb-4 drop-shadow-lg animate-fade-in">
                    {banner.title}
                  </h2>
                  {banner.description && (
                    <p className="text-lg md:text-xl mb-4 drop-shadow-lg opacity-90 animate-fade-in-delay">
                      {banner.description}
                    </p>
                  )}
                  {banner.link_url && (
                    <div className="inline-flex items-center gap-2 text-sm md:text-base opacity-75 group-hover:opacity-100 transition-opacity animate-fade-in-delay-2">
                      <ExternalLink className="w-4 h-4" />
                      Clique para saber mais
                    </div>
                  )}
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Botões de navegação customizados */}
      <button className="swiper-button-prev-custom absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-all duration-200">
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      <button className="swiper-button-next-custom absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-all duration-200">
        <ChevronRight className="w-6 h-6 text-white" />
      </button>

      {/* Estilo customizado para paginação */}
      <style jsx global>{`
        .swiper-pagination {
          bottom: 20px !important;
        }
        .swiper-pagination-bullet {
          background: rgba(255, 255, 255, 0.5) !important;
          width: 12px !important;
          height: 12px !important;
          margin: 0 4px !important;
          opacity: 1 !important;
          transition: all 0.3s ease !important;
        }
        .swiper-pagination-bullet-active {
          background: white !important;
          transform: scale(1.2) !important;
        }
      `}</style>
    </div>
  );
};

export default BannerCarousel;